// src/components/orders/OrderItemsEditModal.jsx
/**
 * Order Items Edit Modal
 * Allows editing of order items with full audit trail compliance (ISO 9001 / SOX)
 * 
 * Features:
 * - Edit existing items (quantity, price, IVA, discount)
 * - Add new items from catalog
 * - Add custom products/services
 * - Remove items
 * - Edit tax fields (cargas impositivas)
 * - Change reason required for audit trail
 * - Confirmation summary before save
 * 
 * Status restriction: Only PENDIENTE or APROBADO orders can be edited
 * Role requirement: ClientAdministrator
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
    Modal, Form, Input, Select, Button, message, Row, Col, Divider, Table, 
    InputNumber, Space, Badge, Alert, Typography, Switch, Tag, Tooltip, Spin
} from 'antd';
import { 
    PlusOutlined, DeleteOutlined, SearchOutlined, AppstoreAddOutlined, 
    ExclamationCircleOutlined, SaveOutlined, UndoOutlined, InfoCircleOutlined,
    CheckCircleOutlined, MinusCircleOutlined, EditOutlined
} from '@ant-design/icons';
import { updateOrderItems, updatePedido } from '../../services/pedidosService';
import { searchProductsBySupplier } from '../../services/productsService';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const OrderItemsEditModal = ({ 
    visible, 
    onClose, 
    onSaved, 
    order,           // Full order object with items
    userRole         // For role-based UI (ClientAdministrator required for API)
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    
    // Items state - starts as copy of order.items
    const [orderItems, setOrderItems] = useState([]);
    const [originalItems, setOriginalItems] = useState([]); // For change detection
    
    // Change reason (required for audit)
    const [changeReason, setChangeReason] = useState('');
    
    // Related supplier invoice (editable)
    const [relatedSupplierInvoice, setRelatedSupplierInvoice] = useState('');
    
    // Tax fields state
    const [cargasImpositivasOn, setCargasImpositivasOn] = useState(false);
    const [taxInputMode, setTaxInputMode] = useState('amount');
    const [retencionAmount, setRetencionAmount] = useState(0);
    const [percepcionAmount, setPercepcionAmount] = useState(0);
    const [iibbDirectoAmount, setIibbDirectoAmount] = useState(0);
    
    // Product search state
    const [searchProduct, setSearchProduct] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchingProducts, setSearchingProducts] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    
    // Custom product modal state
    const [customProductModalVisible, setCustomProductModalVisible] = useState(false);
    const [customProductForm] = Form.useForm();
    
    // Confirmation step
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [changeSummary, setChangeSummary] = useState({ added: [], modified: [], removed: [] });

    // Initialize items when order changes or modal opens
    useEffect(() => {
        if (visible && order) {
            const items = (order.items || []).map((item, index) => ({
                key: item.id || `existing-${index}`,
                original_id: item.id,
                product_id: item.product_id,
                product_code: item.product_code,
                product_description: item.product_name || item.product_description,
                quantity: Number(item.quantity) || 1,
                unit_price: Number(item.unit_price) || 0,
                bulk_price: Number(item.bulk_price) || 0,
                // Use nullish coalescing to preserve 0 as valid value (0 = Exento)
                iva_percentage: item.iva_percentage != null ? Number(item.iva_percentage) : 21,
                discount_percentage: Number(item.discount_percentage) || 0,
                notes: item.notes || '',
                is_custom: !item.product_id || item.product_code === 'CUSTOM',
                is_new: false
            }));
            
            setOrderItems(items);
            setOriginalItems(JSON.parse(JSON.stringify(items))); // Deep copy
            
            // Initialize tax fields from order
            setCargasImpositivasOn(order.cargas_impositivas_on || false);
            setRetencionAmount(Number(order.retencion) || 0);
            setPercepcionAmount(Number(order.percepcion) || 0);
            setIibbDirectoAmount(Number(order.iibb_directo) || 0);
            
            // Initialize related supplier invoice
            setRelatedSupplierInvoice(order.related_supplier_invoice || '');
            
            setChangeReason('');
            setShowConfirmation(false);
        }
    }, [visible, order]);

    // Calculate tax base (subtotal neto - after discounts, before IVA)
    const getTaxBase = () => {
        let subtotalNeto = 0;
        orderItems.forEach(item => {
            const basePrice = (item.bulk_price && item.bulk_price > 0) 
                ? item.bulk_price 
                : item.unit_price;
            const discountAmount = basePrice * (item.discount_percentage / 100);
            const netPrice = basePrice - discountAmount;
            subtotalNeto += item.quantity * netPrice;
        });
        return subtotalNeto;
    };

    // Calculate totals
    const calculateTotals = useMemo(() => {
        let subtotal = 0;
        let totalIVA = 0;
        let totalDiscount = 0;

        orderItems.forEach(item => {
            const basePrice = (item.bulk_price && item.bulk_price > 0) 
                ? item.bulk_price 
                : item.unit_price;
            const discountAmount = basePrice * (item.discount_percentage / 100);
            const netPrice = basePrice - discountAmount;
            const itemSubtotal = item.quantity * netPrice;
            // IVA is always calculated per item based on its iva_percentage
            // (cargas_impositivas toggle only controls retención/percepción/IIBB)
            const itemIVA = itemSubtotal * (item.iva_percentage / 100);

            subtotal += itemSubtotal;
            totalIVA += itemIVA;
            totalDiscount += discountAmount * item.quantity;
        });

        const subtotalWithIVA = subtotal + totalIVA;
        // Additional taxes are controlled by cargas_impositivas toggle
        const percepcion = cargasImpositivasOn ? percepcionAmount : 0;
        const iibbDirecto = cargasImpositivasOn ? iibbDirectoAmount : 0;
        const retencion = cargasImpositivasOn ? retencionAmount : 0;
        const total = subtotalWithIVA + percepcion + iibbDirecto - retencion;

        return { subtotal, iva: totalIVA, discount: totalDiscount, total, percepcion, iibbDirecto, retencion };
    }, [orderItems, cargasImpositivasOn, percepcionAmount, iibbDirectoAmount, retencionAmount]);

    // Handle product search
    const handleProductSearch = async () => {
        if (!searchProduct || searchProduct.trim().length < 2) {
            message.warning('Ingrese al menos 2 caracteres para buscar');
            return;
        }

        if (!order?.supplier_id) {
            message.warning('No se puede buscar productos sin proveedor');
            return;
        }

        try {
            setSearchingProducts(true);
            const response = await searchProductsBySupplier(
                order.supplier_id,
                searchProduct.trim(),
                50
            );

            if (response.success) {
                setSearchResults(response.products || []);
                setShowSearchResults(true);
                
                if (response.products.length === 0) {
                    message.info('No se encontraron productos');
                }
            }
        } catch (error) {
            console.error('Error searching products:', error);
            message.error('Error al buscar productos');
        } finally {
            setSearchingProducts(false);
        }
    };

    // Add product from search
    const handleSelectProduct = (product) => {
        const newItem = {
            key: `new-${Date.now()}`,
            product_id: product.ProductId,
            product_code: product.ProductCode,
            product_description: product.ProductDescription,
            quantity: 1,
            unit_price: product.ProductCost || 0,
            bulk_price: 0,
            iva_percentage: 21.00,
            discount_percentage: 0,
            notes: '',
            is_custom: false,
            is_new: true
        };

        setOrderItems([...orderItems, newItem]);
        setSearchProduct('');
        setSearchResults([]);
        setShowSearchResults(false);
        message.success(`Producto ${product.ProductCode} agregado`);
    };

    // Add custom product
    const handleAddCustomProduct = (values) => {
        const newItem = {
            key: `custom-${Date.now()}`,
            product_id: null,
            product_code: 'CUSTOM',
            product_description: values.product_description.trim(),
            quantity: values.quantity,
            unit_price: values.unit_price,
            bulk_price: 0,
            iva_percentage: values.iva_percentage,
            discount_percentage: values.discount_percentage || 0,
            notes: values.notes || '',
            is_custom: true,
            is_new: true
        };

        setOrderItems([...orderItems, newItem]);
        setCustomProductModalVisible(false);
        customProductForm.resetFields();
        message.success('Producto personalizado agregado');
    };

    // Update item field
    const handleItemChange = (key, field, value) => {
        setOrderItems(orderItems.map(item => 
            item.key === key ? { ...item, [field]: value } : item
        ));
    };

    // Remove item
    const handleRemoveItem = (key) => {
        setOrderItems(orderItems.filter(item => item.key !== key));
    };

    // Detect changes for confirmation summary
    const detectChanges = () => {
        const added = [];
        const modified = [];
        const removed = [];

        // Find added and modified items
        orderItems.forEach(item => {
            if (item.is_new) {
                added.push({
                    product_code: item.product_code,
                    product_description: item.product_description,
                    quantity: item.quantity
                });
            } else {
                const original = originalItems.find(o => o.key === item.key);
                if (original) {
                    const changes = [];
                    if (item.quantity !== original.quantity) changes.push(`Cantidad: ${original.quantity} → ${item.quantity}`);
                    if (item.unit_price !== original.unit_price) changes.push(`Precio: $${original.unit_price} → $${item.unit_price}`);
                    if (item.bulk_price !== original.bulk_price) changes.push(`Costo compra: $${original.bulk_price} → $${item.bulk_price}`);
                    if (item.iva_percentage !== original.iva_percentage) changes.push(`IVA: ${original.iva_percentage}% → ${item.iva_percentage}%`);
                    if (item.discount_percentage !== original.discount_percentage) changes.push(`Descuento: ${original.discount_percentage}% → ${item.discount_percentage}%`);
                    
                    if (changes.length > 0) {
                        modified.push({
                            product_code: item.product_code,
                            product_description: item.product_description,
                            changes
                        });
                    }
                }
            }
        });

        // Find removed items
        originalItems.forEach(original => {
            const stillExists = orderItems.find(item => item.key === original.key);
            if (!stillExists) {
                removed.push({
                    product_code: original.product_code,
                    product_description: original.product_description,
                    quantity: original.quantity
                });
            }
        });

        return { added, modified, removed };
    };

    // Handle pre-save (show confirmation)
    const handlePreSave = () => {
        if (!changeReason.trim()) {
            message.error('Debe ingresar un motivo del cambio para el registro de auditoría');
            return;
        }

        if (orderItems.length === 0) {
            message.error('La orden debe tener al menos un producto');
            return;
        }

        const summary = detectChanges();
        
        // Check if there are any changes
        if (summary.added.length === 0 && summary.modified.length === 0 && summary.removed.length === 0) {
            // Check tax changes
            const taxChanged = 
                cargasImpositivasOn !== (order.cargas_impositivas_on || false) ||
                retencionAmount !== (Number(order.retencion) || 0) ||
                percepcionAmount !== (Number(order.percepcion) || 0) ||
                iibbDirectoAmount !== (Number(order.iibb_directo) || 0);
            
            // Check invoice field change (normalize null/undefined/empty string)
            const originalInvoice = order.related_supplier_invoice || '';
            const newInvoice = relatedSupplierInvoice.trim();
            const invoiceChanged = newInvoice !== originalInvoice;
            
            if (!taxChanged && !invoiceChanged) {
                message.info('No hay cambios para guardar');
                return;
            }
        }

        setChangeSummary(summary);
        setShowConfirmation(true);
    };

    // Handle actual save
    const handleSave = async () => {
        setLoading(true);
        try {
            // Check if related_supplier_invoice changed (uses separate endpoint)
            const originalInvoice = order.related_supplier_invoice || '';
            const newInvoice = relatedSupplierInvoice.trim();
            const invoiceChanged = newInvoice !== originalInvoice;
            
            // Check if items or tax fields changed
            const summary = detectChanges();
            const itemsChanged = summary.added.length > 0 || summary.modified.length > 0 || summary.removed.length > 0;
            const taxChanged = 
                cargasImpositivasOn !== (order.cargas_impositivas_on || false) ||
                retencionAmount !== (Number(order.retencion) || 0) ||
                percepcionAmount !== (Number(order.percepcion) || 0) ||
                iibbDirectoAmount !== (Number(order.iibb_directo) || 0);
            
            let updatedOrder = null;
            let auditInfo = null;
            
            // 1. Update related_supplier_invoice if changed (PUT /orders/:id)
            // Backend uses COALESCE so null won't clear the field - use marker text instead
            if (invoiceChanged) {
                const invoiceValue = newInvoice || 'SIN FACTURA CARGADA';
                const invoiceResponse = await updatePedido(order.id, {
                    related_supplier_invoice: invoiceValue
                });
                if (invoiceResponse.success) {
                    updatedOrder = invoiceResponse.data;
                }
            }
            
            // 2. Update items and tax fields if changed (PUT /orders/:id/items)
            if (itemsChanged || taxChanged) {
                const requestData = {
                    change_reason: changeReason.trim(),
                    items: orderItems.map(item => ({
                        product_id: item.product_id || null,
                        product_code: item.product_code,
                        product_description: item.product_description,
                        quantity: item.quantity,
                        unit_price: item.unit_price,
                        bulk_price: item.bulk_price || 0,
                        iva_percentage: item.iva_percentage,
                        discount_percentage: item.discount_percentage || 0,
                        notes: item.notes || null
                    })),
                    tax_fields: {
                        cargas_impositivas_on: cargasImpositivasOn,
                        retencion: retencionAmount,
                        percepcion: percepcionAmount,
                        iibb_directo: iibbDirectoAmount
                    }
                };

                const response = await updateOrderItems(order.id, requestData);

                if (response.success) {
                    updatedOrder = response.data?.order;
                    auditInfo = response.data?.audit || {};
                }
            }

            // Show success message
            if (auditInfo) {
                message.success(
                    `Items actualizados. Agregados: ${auditInfo.items_added || 0}, ` +
                    `Modificados: ${auditInfo.items_modified || 0}, ` +
                    `Eliminados: ${auditInfo.items_removed || 0}` +
                    (invoiceChanged ? '. Factura del proveedor actualizada.' : '')
                );
            } else if (invoiceChanged) {
                message.success('Factura del proveedor actualizada');
            }
            
            onSaved(updatedOrder);
            handleClose();
        } catch (error) {
            console.error('Error updating order:', error);
            message.error(error.message || 'Error al actualizar la orden');
        } finally {
            setLoading(false);
        }
    };

    // Handle close/reset
    const handleClose = () => {
        setOrderItems([]);
        setOriginalItems([]);
        setChangeReason('');
        setRelatedSupplierInvoice('');
        setShowConfirmation(false);
        setSearchProduct('');
        setSearchResults([]);
        setShowSearchResults(false);
        onClose();
    };

    // Handle back from confirmation
    const handleBackFromConfirmation = () => {
        setShowConfirmation(false);
    };

    // Check if user can edit
    const canEdit = userRole === 'ClientAdministrator' || userRole === 'admin';
    const isEditableStatus = order?.status === 'PENDIENTE' || order?.status === 'APROBADO';

    // Table columns for items
    const itemColumns = [
        {
            title: 'Código',
            dataIndex: 'product_code',
            key: 'product_code',
            width: 120,
            render: (text, record) => (
                <Space size="small">
                    <span>{text}</span>
                    {record.is_custom && (
                        <Badge 
                            count="CUSTOM" 
                            style={{ backgroundColor: '#722ed1', fontSize: '9px', padding: '0 3px' }} 
                        />
                    )}
                    {record.is_new && (
                        <Badge 
                            count="NUEVO" 
                            style={{ backgroundColor: '#52c41a', fontSize: '9px', padding: '0 3px' }} 
                        />
                    )}
                </Space>
            ),
        },
        {
            title: 'Descripción',
            dataIndex: 'product_description',
            key: 'product_description',
            ellipsis: true,
        },
        {
            title: 'Cantidad',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 90,
            render: (value, record) => (
                <InputNumber
                    value={value}
                    onChange={(v) => handleItemChange(record.key, 'quantity', v)}
                    min={1}
                    size="small"
                    style={{ width: '100%' }}
                />
            ),
        },
        {
            title: 'P. Unitario',
            dataIndex: 'unit_price',
            key: 'unit_price',
            width: 110,
            render: (value, record) => (
                <InputNumber
                    value={value}
                    onChange={(v) => handleItemChange(record.key, 'unit_price', v)}
                    min={0}
                    precision={2}
                    size="small"
                    style={{ width: '100%' }}
                    prefix="$"
                    disabled={!record.is_custom && !record.is_new}
                />
            ),
        },
        {
            title: 'Costo Compra',
            dataIndex: 'bulk_price',
            key: 'bulk_price',
            width: 110,
            render: (value, record) => (
                <InputNumber
                    value={value}
                    onChange={(v) => handleItemChange(record.key, 'bulk_price', v)}
                    min={0}
                    precision={2}
                    size="small"
                    style={{ width: '100%' }}
                    prefix="$"
                    placeholder="0"
                />
            ),
        },
        {
            title: 'IVA %',
            dataIndex: 'iva_percentage',
            key: 'iva_percentage',
            width: 90,
            render: (value, record) => (
                <Select
                    value={value}
                    onChange={(v) => handleItemChange(record.key, 'iva_percentage', v)}
                    size="small"
                    style={{ width: '100%' }}
                >
                    <Option value={0}>0%</Option>
                    <Option value={10.5}>10.5%</Option>
                    <Option value={21}>21%</Option>
                </Select>
            ),
        },
        {
            title: 'Desc %',
            dataIndex: 'discount_percentage',
            key: 'discount_percentage',
            width: 80,
            render: (value, record) => (
                <InputNumber
                    value={value}
                    onChange={(v) => handleItemChange(record.key, 'discount_percentage', v)}
                    min={0}
                    max={99.99}
                    precision={2}
                    size="small"
                    style={{ width: '100%' }}
                />
            ),
        },
        {
            title: 'Subtotal',
            key: 'subtotal',
            width: 100,
            align: 'right',
            render: (_, record) => {
                const basePrice = (record.bulk_price && record.bulk_price > 0) ? record.bulk_price : record.unit_price;
                const discount = basePrice * (record.discount_percentage / 100);
                const netPrice = basePrice - discount;
                const subtotal = record.quantity * netPrice;
                return <Text strong>${subtotal.toFixed(2)}</Text>;
            },
        },
        {
            title: '',
            key: 'actions',
            width: 50,
            fixed: 'right',
            render: (_, record) => (
                <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveItem(record.key)}
                    title="Quitar producto"
                />
            ),
        },
    ];

    // Format price helper
    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return 'N/A';
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Render confirmation view
    const renderConfirmation = () => {
        const invoiceChanged = relatedSupplierInvoice !== (order?.related_supplier_invoice || '');
        
        return (
        <div>
            <Alert
                message="Confirmar Cambios"
                description={`Los siguientes cambios se aplicarán a la orden ${order?.order_number}`}
                type="info"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ marginBottom: 16 }}
            />

            <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                <Text strong>Motivo del cambio:</Text>
                <div style={{ marginTop: 4, fontStyle: 'italic' }}>"{changeReason}"</div>
            </div>

            {invoiceChanged && (
                <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '4px' }}>
                    <Text strong style={{ color: '#1890ff' }}>
                        <EditOutlined /> Factura del Proveedor:
                    </Text>
                    <div style={{ marginTop: 4 }}>
                        {order?.related_supplier_invoice || <span style={{ color: '#999' }}>(vacío)</span>}
                        {' → '}
                        {relatedSupplierInvoice || <span style={{ color: '#999' }}>(vacío)</span>}
                    </div>
                </div>
            )}

            {changeSummary.added.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ color: '#52c41a' }}>
                        <CheckCircleOutlined /> Items a agregar ({changeSummary.added.length}):
                    </Text>
                    <ul style={{ marginTop: 8 }}>
                        {changeSummary.added.map((item, i) => (
                            <li key={i}>
                                <Tag color="green">{item.product_code}</Tag> 
                                {item.product_description} - Cantidad: {item.quantity}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {changeSummary.modified.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ color: '#1890ff' }}>
                        <EditOutlined /> Items a modificar ({changeSummary.modified.length}):
                    </Text>
                    <ul style={{ marginTop: 8 }}>
                        {changeSummary.modified.map((item, i) => (
                            <li key={i}>
                                <Tag color="blue">{item.product_code}</Tag> 
                                {item.product_description}
                                <ul style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
                                    {item.changes.map((change, j) => (
                                        <li key={j}>{change}</li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {changeSummary.removed.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ color: '#ff4d4f' }}>
                        <MinusCircleOutlined /> Items a eliminar ({changeSummary.removed.length}):
                    </Text>
                    <ul style={{ marginTop: 8 }}>
                        {changeSummary.removed.map((item, i) => (
                            <li key={i}>
                                <Tag color="red">{item.product_code}</Tag> 
                                {item.product_description} - Cantidad: {item.quantity}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <Divider />

            <Row gutter={16}>
                <Col span={12}>
                    <Text type="secondary">Total anterior: {formatPrice(Number(order?.total) || 0)}</Text>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                    <Text strong style={{ fontSize: '16px' }}>Nuevo total: {formatPrice(calculateTotals.total)}</Text>
                </Col>
            </Row>
        </div>
    );
    };

    // Render edit view
    const renderEditView = () => (
        <div>
            {/* Order Info Header */}
            <Alert
                message={`Editando: ${order?.order_number}`}
                description={`Proveedor: ${order?.supplier_razon_social || 'N/A'}`}
                type="info"
                style={{ marginBottom: 16 }}
            />

            {/* Change Reason - Required */}
            <div style={{ marginBottom: 16 }}>
                <Text strong style={{ color: '#ff4d4f' }}>* Motivo del cambio (requerido para auditoría):</Text>
                <TextArea
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                    placeholder="Ej: Corrección de precios según factura FC-A-0007-00048350"
                    rows={2}
                    style={{ marginTop: 8 }}
                    maxLength={500}
                    showCount
                />
            </div>

            {/* Related Supplier Invoice */}
            <div style={{ marginBottom: 16 }}>
                <Text strong>Factura del Proveedor Relacionada:</Text>
                <Input
                    value={relatedSupplierInvoice}
                    onChange={(e) => setRelatedSupplierInvoice(e.target.value)}
                    placeholder="Ej: FC-A-0007-00048350"
                    style={{ marginTop: 8 }}
                    maxLength={50}
                />
            </div>

            <Divider orientation="left" style={{ margin: '12px 0' }}>Productos</Divider>

            {/* Product Search */}
            <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col flex="auto">
                    <Input
                        placeholder="Buscar producto por código o descripción..."
                        value={searchProduct}
                        onChange={(e) => setSearchProduct(e.target.value)}
                        onPressEnter={handleProductSearch}
                        prefix={<SearchOutlined />}
                        suffix={searchingProducts && <Spin size="small" />}
                    />
                </Col>
                <Col>
                    <Button 
                        type="primary" 
                        onClick={handleProductSearch}
                        loading={searchingProducts}
                    >
                        Buscar
                    </Button>
                </Col>
                <Col>
                    <Button 
                        icon={<AppstoreAddOutlined />}
                        onClick={() => setCustomProductModalVisible(true)}
                    >
                        Agregar Personalizado
                    </Button>
                </Col>
            </Row>

            {/* Search Results */}
            {showSearchResults && searchResults.length > 0 && (
                <div style={{ 
                    marginBottom: 12, 
                    padding: 8, 
                    backgroundColor: '#f0f5ff', 
                    borderRadius: 4,
                    maxHeight: 200,
                    overflowY: 'auto'
                }}>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>
                        Resultados ({searchResults.length}):
                    </Text>
                    {searchResults.map(product => (
                        <div 
                            key={product.ProductId}
                            style={{ 
                                padding: '6px 8px', 
                                marginBottom: 4, 
                                backgroundColor: 'white',
                                borderRadius: 4,
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                            onClick={() => handleSelectProduct(product)}
                        >
                            <span>
                                <Tag>{product.ProductCode}</Tag>
                                {product.ProductDescription}
                            </span>
                            <span style={{ color: '#1890ff' }}>
                                ${product.ProductCost?.toFixed(2) || '0.00'}
                            </span>
                        </div>
                    ))}
                    <Button 
                        size="small" 
                        onClick={() => setShowSearchResults(false)}
                        style={{ marginTop: 8 }}
                    >
                        Cerrar resultados
                    </Button>
                </div>
            )}

            {/* Items Table */}
            <Table
                columns={itemColumns}
                dataSource={orderItems}
                rowKey="key"
                pagination={false}
                size="small"
                scroll={{ x: 900, y: 300 }}
                bordered
            />

            {/* Tax Fields Section */}
            <Divider orientation="left" style={{ margin: '16px 0 12px 0' }}>
                Cargas Impositivas
                <Tooltip title="Habilitar para incluir retención, percepción e IIBB en el total">
                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                </Tooltip>
            </Divider>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Space>
                        <Switch 
                            checked={cargasImpositivasOn} 
                            onChange={setCargasImpositivasOn}
                            size="small"
                        />
                        <Text>{cargasImpositivasOn ? 'Activas' : 'Inactivas'}</Text>
                    </Space>
                </Col>
                
                {cargasImpositivasOn && (
                    <>
                        <Col span={6}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Retención (resta):</Text>
                            <InputNumber
                                value={retencionAmount}
                                onChange={setRetencionAmount}
                                min={0}
                                precision={2}
                                style={{ width: '100%' }}
                                prefix="$"
                                size="small"
                            />
                        </Col>
                        <Col span={6}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Percepción (suma):</Text>
                            <InputNumber
                                value={percepcionAmount}
                                onChange={setPercepcionAmount}
                                min={0}
                                precision={2}
                                style={{ width: '100%' }}
                                prefix="$"
                                size="small"
                            />
                        </Col>
                        <Col span={6}>
                            <Text type="secondary" style={{ fontSize: 12 }}>IIBB Directo (suma):</Text>
                            <InputNumber
                                value={iibbDirectoAmount}
                                onChange={setIibbDirectoAmount}
                                min={0}
                                precision={2}
                                style={{ width: '100%' }}
                                prefix="$"
                                size="small"
                            />
                        </Col>
                    </>
                )}
            </Row>

            {/* Totals Summary */}
            <div style={{ 
                backgroundColor: '#f5f5f5', 
                padding: 12, 
                borderRadius: 4,
                marginTop: 16 
            }}>
                <Row gutter={16}>
                    <Col span={6}>
                        <Text type="secondary">Subtotal:</Text>
                        <div><Text strong>{formatPrice(calculateTotals.subtotal)}</Text></div>
                    </Col>
                    <Col span={6}>
                        <Text type="secondary">IVA:</Text>
                        <div><Text strong>{formatPrice(calculateTotals.iva)}</Text></div>
                    </Col>
                    {calculateTotals.discount > 0 && (
                        <Col span={6}>
                            <Text type="secondary" style={{ color: '#52c41a' }}>Descuentos:</Text>
                            <div><Text strong style={{ color: '#52c41a' }}>-{formatPrice(calculateTotals.discount)}</Text></div>
                        </Col>
                    )}
                    <Col span={6}>
                        <Text type="secondary">TOTAL:</Text>
                        <div><Text strong style={{ fontSize: 18, color: '#1890ff' }}>{formatPrice(calculateTotals.total)}</Text></div>
                    </Col>
                </Row>
            </div>
        </div>
    );

    return (
        <>
            <Modal
                title={
                    <Space>
                        <EditOutlined />
                        {showConfirmation ? 'Confirmar Cambios' : 'Editar Items de la Orden'}
                    </Space>
                }
                open={visible}
                onCancel={handleClose}
                width={1100}
                style={{ top: 20 }}
                footer={showConfirmation ? [
                    <Button 
                        key="back" 
                        onClick={handleBackFromConfirmation}
                        icon={<UndoOutlined />}
                        disabled={loading}
                    >
                        Volver a editar
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleSave}
                        loading={loading}
                        icon={<SaveOutlined />}
                    >
                        Confirmar y Guardar
                    </Button>
                ] : [
                    <Button key="cancel" onClick={handleClose}>
                        Cancelar
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handlePreSave}
                        disabled={!changeReason.trim() || orderItems.length === 0}
                        icon={<SaveOutlined />}
                    >
                        Revisar Cambios
                    </Button>
                ]}
            >
                {!canEdit && (
                    <Alert
                        message="Permisos insuficientes"
                        description="Solo usuarios con rol ClientAdministrator pueden editar items de órdenes."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                {!isEditableStatus && (
                    <Alert
                        message="Estado no editable"
                        description={`Las órdenes con estado "${order?.status}" no pueden ser editadas. Solo órdenes PENDIENTE o APROBADO.`}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                {showConfirmation ? renderConfirmation() : renderEditView()}
            </Modal>

            {/* Custom Product Modal */}
            <Modal
                title={<Space><AppstoreAddOutlined /> Agregar Producto/Servicio Personalizado</Space>}
                open={customProductModalVisible}
                onCancel={() => {
                    setCustomProductModalVisible(false);
                    customProductForm.resetFields();
                }}
                onOk={() => customProductForm.submit()}
                okText="Agregar"
                cancelText="Cancelar"
            >
                <Form
                    form={customProductForm}
                    layout="vertical"
                    onFinish={handleAddCustomProduct}
                    initialValues={{
                        quantity: 1,
                        iva_percentage: 21,
                        discount_percentage: 0
                    }}
                >
                    <Form.Item
                        name="product_description"
                        label="Descripción"
                        rules={[{ required: true, message: 'Ingrese una descripción' }]}
                    >
                        <Input placeholder="Ej: Servicio de instalación" />
                    </Form.Item>
                    
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="quantity"
                                label="Cantidad"
                                rules={[{ required: true, message: 'Ingrese cantidad' }]}
                            >
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="unit_price"
                                label="Precio Unitario"
                                rules={[{ required: true, message: 'Ingrese precio' }]}
                            >
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="iva_percentage" label="IVA %">
                                <Select>
                                    <Option value={0}>0% (Exento)</Option>
                                    <Option value={10.5}>10.5% (Reducido)</Option>
                                    <Option value={21}>21% (General)</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="discount_percentage" label="Descuento %">
                                <InputNumber min={0} max={99.99} precision={2} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="notes" label="Notas (opcional)">
                        <TextArea rows={2} placeholder="Observaciones adicionales" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default OrderItemsEditModal;
